import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getDomainRestrictionsForItems } from 'reactApp/appHelpers/appHelpers';
import { NEW_PORTAL_HEADER } from 'reactApp/appHelpers/configHelpers';
import { hasNewTableGrid } from 'reactApp/appHelpers/featuresHelpers/features/datalistNewTableGrid';
import { fileTypesForAiOrganize } from 'reactApp/appHelpers/featuresHelpers/features/fakedoorNeuroOrganizePromo';
import { enabledPublicDownloadLimits } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import DownloadLimitsInformer from 'reactApp/components/DownloadLimitsInformer/DownloadLimitsInformer';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { selectFromCloudGetSelected } from 'reactApp/modules/albums/albums.selector';
import { EAllDocumentsType } from 'reactApp/modules/allDocuments/allDocuments.types';
import type { EditorItem } from 'reactApp/modules/editor/editor.types';
import { getFeatureShowStories } from 'reactApp/modules/features/features.selectors';
import { getLastFilesForWidget } from 'reactApp/modules/feed/feed.selectors';
import { FAKE_GALLERY_ITEM_ID } from 'reactApp/modules/gallery/gallery.constants';
import { getCurrentFolderHome } from 'reactApp/modules/home/home.selectors';
import { isOnboardingHintVisible } from 'reactApp/modules/onboardingSteps/onboardingSteps.selector';
import { getFirstPublicWithDownloadLeftZero } from 'reactApp/modules/public/public.selectors';
import { getCurrentSharedTab } from 'reactApp/modules/shared/shared.selectors';
import { SharedTab } from 'reactApp/modules/shared/shared.type';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { type CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { getStorySummaryCovers } from 'reactApp/modules/stories/stories.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { GalleryPageTabMenu } from 'reactApp/sections/GalleryPage/GalleryPageTabMenu';
import type { RootState } from 'reactApp/store';
import { BreadcrumbsContainer } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { isMobile } from 'reactApp/ui/Datalist/DataList.constants';
import {
    DATA_LIST_BREADCRUMBS_ID,
    DATA_LIST_EXTRABLOCKS_ID,
    DATA_LIST_GALLERY_TAB_MENU_ID,
    DATA_LIST_LAST_FILES_WIDGET_ID,
    DATA_LIST_PUBLIC_UPLOAD_HEADER_ID,
    DATA_LIST_STORIES_WIDGET_ID,
    FAMILY_DOCUMENTS_FAKEDOOR_ID,
    SAFE_FAKEDOOR_ID,
} from 'reactApp/ui/Datalist/DataList.ids';
import type { Props as DataListProps } from 'reactApp/ui/Datalist/DataList.types';
import { DataListGalleryItemStub } from 'reactApp/ui/DataListGalleryItemStub/DataListGalleryItemStub';
import { DataListItem } from 'reactApp/ui/DataListItem/DataListItem';
import type { DataListItemType } from 'reactApp/ui/DataListItem/DataListItem.types';
import { DataListItemCreateNew, ID_NEW_ITEM } from 'reactApp/ui/DataListItemCreateNew/DataListItemCreateNew';
import { CreateNewMod } from 'reactApp/ui/DataListItemCreateNew/DataListItemCreateNew.data';
import { DataListItemFamilyDocumentsFakeDoor } from 'reactApp/ui/DataListItemFamilyDocumentsFakeDoor/DataListItemFamilyDocumentsFakeDoor';
import { DataListItemSafeFakedoor } from 'reactApp/ui/DataListItemSafeFakedoor/DataListItemSafeFakedoor';
import type { LastFilesWidgetProps } from 'reactApp/ui/LastFilesWidget/LastFilesWidget';
import { PublicUploadHeader } from 'reactApp/ui/PublicUploadHeader/PublicUploadHeader';
import { StoriesWidget } from 'reactApp/ui/StoriesWidget/StoriesWidget';

import { ExtraBlocks } from '../ExtraBlocks/ExtraBlocks';
import styles from './DataListItem.css';

interface IProps extends Omit<DataListItemType, 'selectableKey' | 'isSelected' | 'isSelecting' | 'pos' | 'item'> {
    optionIndex: number;
    handleClickCreateNew: (e) => void;
    isSharedLinks: boolean;
    selectingIdxs: string[];
    activeIndex: number | null;
    dragOverItemId: string | undefined;
    selectedCount: number;
    rootRef?: React.Ref<HTMLDivElement>;
    isPopup?: boolean;
    currentAllDocsType?: EAllDocumentsType | null;
    itemsAmount: number;
    isSharedAutodelete?: boolean;
    onItemSizeChange?: (size: number) => void;
    hideUpload?: boolean;
    hideRestore?: boolean;
    onAuthorContextMenuItem: (event: any) => void;
    isShowAiTooltip?: boolean;
    isShowAiPhotoImprovingTooltip?: boolean;
    LastFilesWidget: React.ComponentType<LastFilesWidgetProps>;
    DataList: React.ComponentType<DataListProps>;
}

// eslint-disable-next-line max-lines-per-function, complexity
export const WrapDataListItem = ({
    id,
    optionIndex,
    view,
    handleClickCreateNew,
    isSharedLinks,
    selectedIdxs,
    selectingIdxs,
    activeIndex,
    isDragging,
    dragOverItemId,
    onSelect,
    onClick,
    onContextMenu,
    onAuthorContextMenuItem,
    selectedCount,
    storage,
    tipData,
    isFavoritesEnabled,
    onDraggingOver,
    onMouseLeave,
    register,
    unregister,
    onMouseDown,
    rootRef,
    thumbType,
    isPopup,
    isPublicDownloaded,
    currentAllDocsType,
    listOfBannedToFavoriteItems,
    albumShowSelected = false,
    itemsAmount,
    onItemSizeChange,
    hideUpload,
    hideRestore,
    canShowSharedFileAuthor,
    isShowAiTooltip,
    isShowAiPhotoImprovingTooltip,
    LastFilesWidget,
    DataList,
}: IProps) => {
    const storeStories = useSelector(getStorySummaryCovers);
    const userHasStories = storeStories?.length > 0;
    const isShowStoryEnabled = useSelector(getFeatureShowStories);
    const lastFiles = useSelector(getLastFilesForWidget);
    const currentTab = useSelector(getCurrentSharedTab);
    const { isDomainAndDownloadRestricted, isDomainAndShareRestricted } = getDomainRestrictionsForItems(id);
    // Подсвечивает кнопку кнопки шаринга (не убирает из DOM)
    const isPublicButtonVisible = useSelector((state: RootState) => isOnboardingHintVisible(state, { id }));
    const isPublishButtonVisible = !isDomainAndShareRestricted;
    const isDownloadButtonVisible = !isDomainAndDownloadRestricted;

    const storageItem = useSelector((state: RootState) => getStorageItemById(state, storage, id)) as Exclude<CloudItem, EditorItem>;
    const selectedItem = useSelector((state: RootState) => selectFromCloudGetSelected(state, id)) as Exclude<CloudItem, EditorItem>;
    const item = albumShowSelected ? selectedItem : storageItem;

    // tempexp_17847-start
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const firstPublicWithDownloadLeftZero = useSelector((state: RootState) => getFirstPublicWithDownloadLeftZero(state));
    const valuePublicLimits = firstPublicWithDownloadLeftZero && Object.values(firstPublicWithDownloadLeftZero)[0];
    const leftDownloads = valuePublicLimits?.left;
    const totalDownloads = valuePublicLimits?.total;

    const currentFolfder = useSelector(getCurrentFolderHome);
    const isHome = currentFolfder?.id === ROOT_FOLDER_ID;
    const showDownloadLimitsInformer =
        enabledPublicDownloadLimits &&
        (storage === EStorageType.home || storage === EStorageType.sharedLinks) &&
        !isPaidUser &&
        leftDownloads === 0;
    // tempexp_17847-end

    const handleLastFilesWidgetExpand = useCallback(
        (expanded) => {
            onItemSizeChange?.(expanded ? 244 : 50);
        },
        [onItemSizeChange]
    );

    if (id === ID_NEW_ITEM) {
        let mod: CreateNewMod | undefined;
        if (storage === EStorageType.alldocuments) {
            // eslint-disable-next-line sonarjs/no-small-switch
            switch (currentAllDocsType) {
                case EAllDocumentsType.spreadsheet:
                    mod = CreateNewMod.newAllDocumentSpreadsheet;
                    break;
                case EAllDocumentsType.presentation:
                    mod = CreateNewMod.newAllDocumentPresentation;
                    break;
                default:
                    mod = CreateNewMod.newAllDocument;
                    break;
            }
        }

        if (storage === EStorageType.sharedLinks && currentTab === SharedTab.indexed) {
            mod = CreateNewMod.sharedIndexed;
        }

        if (storage === EStorageType.familyFolders) {
            mod = CreateNewMod.familyFolders;
        }

        return hasNewTableGrid(storage) ? (
            <DataListItemCreateNew.New
                isThumb={view === 'thumbs'}
                onClick={handleClickCreateNew}
                onContextMenu={handleClickCreateNew}
                isSharedFolderOnly={isSharedLinks}
                rootRef={rootRef}
                storage={storage}
                mod={mod}
            />
        ) : (
            <DataListItemCreateNew
                isThumb={view === 'thumbs'}
                onClick={handleClickCreateNew}
                onContextMenu={handleClickCreateNew}
                isSharedFolderOnly={isSharedLinks}
                rootRef={rootRef}
                storage={storage}
                mod={mod}
            />
        );
    }

    if (id === DATA_LIST_BREADCRUMBS_ID && NEW_PORTAL_HEADER) {
        return enabledPublicDownloadLimits && !isPaidUser && !isHome ? (
            <div className={styles.breadcrumbs_new_portal_header}>
                <BreadcrumbsContainer />
                {showDownloadLimitsInformer && <DownloadLimitsInformer totalDownloads={totalDownloads} />}
            </div>
        ) : (
            <BreadcrumbsContainer className={styles.breadcrumbs_new_portal_header} />
        );
    }

    if (id === DATA_LIST_EXTRABLOCKS_ID && NEW_PORTAL_HEADER) {
        return <ExtraBlocks itemsCount={itemsAmount} hideUpload={hideUpload} />;
    }

    if (id === DATA_LIST_STORIES_WIDGET_ID && isShowStoryEnabled && userHasStories && NEW_PORTAL_HEADER) {
        return <StoriesWidget stories={storeStories} />;
    }

    if (id === DATA_LIST_LAST_FILES_WIDGET_ID && lastFiles.length > 0) {
        return <LastFilesWidget lastFiles={lastFiles} onExpand={handleLastFilesWidgetExpand} DataList={DataList} />;
    }

    if (id === SAFE_FAKEDOOR_ID) {
        return <DataListItemSafeFakedoor viewMode={view} />;
    }

    if (id === FAMILY_DOCUMENTS_FAKEDOOR_ID) {
        return <DataListItemFamilyDocumentsFakeDoor viewMode={view} />;
    }

    if (id === DATA_LIST_GALLERY_TAB_MENU_ID) {
        return <GalleryPageTabMenu />;
    }

    if (id === DATA_LIST_PUBLIC_UPLOAD_HEADER_ID) {
        return <PublicUploadHeader />;
    }

    if (id === FAKE_GALLERY_ITEM_ID) {
        return <DataListGalleryItemStub />;
    }

    const isSelected = selectedIdxs.includes(id);
    const isSelecting = selectingIdxs.includes(id);
    const isActive = activeIndex === optionIndex;

    if (!item) {
        return null;
    }

    return (
        <DataListItem
            id={id}
            item={item}
            isDragging={isDragging}
            isSelected={isSelected}
            isActive={isActive}
            isSelecting={isSelecting}
            selectedIdxs={selectedIdxs}
            isDragOver={isDragging && dragOverItemId === id && !isSelected}
            onSelect={onSelect}
            onClick={onClick}
            onContextMenu={onContextMenu}
            onAuthorContextMenuItem={onAuthorContextMenuItem}
            canShowSharedFileAuthor={canShowSharedFileAuthor}
            view={view || 'thumbs'}
            showCheckbox={selectedCount >= 1 || isSelected}
            storage={storage || EStorageType.home}
            isMobile={isMobile}
            tipData={tipData}
            isFavoritesEnabled={isFavoritesEnabled}
            listOfBannedToFavoriteItems={listOfBannedToFavoriteItems}
            onDraggingOver={onDraggingOver}
            onMouseLeave={onMouseLeave}
            selectableKey={id}
            register={register}
            unregister={unregister}
            onMouseDown={onMouseDown}
            thumbType={thumbType}
            isPopup={isPopup}
            pos={optionIndex}
            isPublicDownloaded={isPublicDownloaded}
            albumShowSelected={albumShowSelected}
            showPublicButton={isPublicButtonVisible}
            showPublishButton={isPublishButtonVisible}
            showDownloadButton={isDownloadButtonVisible}
            hideRestore={hideRestore}
            isShowAiTooltip={
                isShowAiTooltip && fileTypesForAiOrganize.some((extension) => item.isFolder || ('ext' in item && item.ext === extension))
            }
            isShowAiPhotoImprovingTooltip={isShowAiPhotoImprovingTooltip}
        />
    );
};
